import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { CompanyControllerService } from '@iota/iota-management-portal-v4-client';
import { catchError, map, mergeMap, take } from 'rxjs/operators';
import {
    CreateOffice,
    CreateOfficeSuccess,
    LoadAllOffices,
    OfficeActionTypes,
    ReloadOffice,
    UpdateOffice
} from '../actions/office.actions';
import { LoadUserSuccess } from '../actions/user.actions';
import { OfficeManagementControllerService } from '@iota/inh-core-v1-management-client';

@Injectable()
export class OfficeEffects {
    // TODO: only load all offices when needed, but it's difficult in certain components
    // for example in employee-card. This would lead to many requests to load every single office
    // or load all offices anyway. Because these components are used in many places,
    // we would need to check if they are loaded very often, so currently this seems like the best solution.
    loadAllOffices$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadAllOffices, LoadUserSuccess),
            mergeMap(() =>
                this.apiService.getAllOffices().pipe(
                    take(1),
                    map((offices) => ({
                        type: OfficeActionTypes.LoadAllOfficesSuccess,
                        offices
                    })),
                    catchError((error) => of({ type: OfficeActionTypes.LoadOfficesFailure, error: error }))
                )
            )
        )
    );

    reloadOffice$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReloadOffice, CreateOfficeSuccess),
            mergeMap(({ officeId }) =>
                this.apiService.getOffice(officeId).pipe(
                    take(1),
                    map((office) => ({
                        type: OfficeActionTypes.ReloadOfficeSuccess,
                        office
                    })),
                    catchError((error) => of({ type: OfficeActionTypes.ReloadOfficeFailure, error: error }))
                )
            )
        )
    );

    updateOffice = createEffect(() =>
        this.actions$.pipe(
            ofType(UpdateOffice),
            mergeMap(({ id, request }) =>
                this.inhCoreOfficeService.update(id, request).pipe(
                    take(1),
                    map(() => ({
                        type: OfficeActionTypes.ReloadOffice,
                        officeId: id
                    })),
                    catchError((error) => of({ type: OfficeActionTypes.OfficeActionFailure, error: error }))
                )
            )
        )
    );

    createOffice$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CreateOffice),
            mergeMap(({ office }) =>
                this.inhCoreOfficeService
                    .create(
                        {
                            companyId: office.companyId,
                            name: office.name,
                            address: {
                                countryCode: office.addresses[0].countryCode,
                                country: office.addresses[0].country,
                                city: office.addresses[0].city,
                                postOfficeBox: office.addresses[0].postOfficeBox,
                                street: office.addresses[0].street,
                                street2: office.addresses[0].street2,
                                houseNumber: office.addresses[0].houseNumber,
                                zipCode: office.addresses[0].zipCode,
                                type: office.addresses[0].addressType,
                                name1: office.addresses[0].name1,
                                name2: office.addresses[0].name2
                            }
                        },
                        'response'
                    )
                    .pipe(
                        take(1),
                        map((response) => ({
                            type: OfficeActionTypes.CreateOfficeSuccess,
                            officeId: response.body.officeId
                        })),
                        catchError((error) => of({ type: OfficeActionTypes.OfficeActionFailure, error: error }))
                    )
            )
        )
    );

    constructor(
        private actions$: Actions,
        private apiService: CompanyControllerService,
        private inhCoreOfficeService: OfficeManagementControllerService
    ) {}
}
